import { type JanusCtx, type Row, ec, ecId, ecLean, handBack, insertRoll, itemField, record, refuse, requiredInt, upper } from './context.js';
import { type JanusInput } from './janus.js';
import { applyStatusEffect } from './statuses/apply-status-effect.js';
import { getStatusEffectDurationTimes } from './statuses/get-status-effect-duration-times.js';
import { CUSTOM_TYPES, SUMMON_CONTROL_IDS } from './rolls/registry-dispatch-rolls.js';
import { isSummonAbility, isSummonPickVariable, summonCreatureId } from './summon.js';
import { isSpellGrantCast } from './spell-grant-mode.js';
import { creatureTypeCastPreflight } from './creature-type-gate.js';

const SPOT_HIDDEN_CREATURES = 'B2D3417B-2700-11CB-E17D-38DECAF66F1A';

const CASTING_STATUS = '5D939B6E-B9BC-7AC5-5143-4F00F4189EA3';
const RITUAL_STATUS = '5833A733-D277-0A60-3A94-A0F355A79A80';
const CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
const SEARCHING_STATUS = 'AE967694-B94B-F0FF-B680-829FDE6E8D58';
const SEARCH_FOR_HIDDEN_INVENTORY = '5B518ED7-4CEF-72BE-F20B-912598267714';

/** The explicit ritual choice for an authored summon uses the existing ritual finish lane. */
export function isRitualSummonStart(ctx: JanusCtx, input: JanusInput, ability: Row): boolean {
  return input.useAbilityVariable1 === 'spell_ritual' && itemField(ability, 'item_type') === 'spell'
    && ctx.read.has(ability, 'spell_ritual') && isSummonAbility(ctx, ability);
}

/**
 * casting_roll.php's DELAYED START of a timed cast (spell_casting_time one_minute … twenty_four_hours) — Janus lane A9 (2026-10-02, R-108
 * bucket `casting_roll:target-storage-not-held`; was a hand-back). The start costs 3 AP while the encounter is in combat (process_action_points:
 * a trigger frame or an out-of-combat encounter spends nothing; below 0 → "Not enough action points!"), stamps the Casting status on the
 * caster — origin = the cast, status_casting_targets_arr holds the request's selection plus its numeric cast level when selected,
 * no log row — and logs `casting` (roll_type_2 = the cast). The observing browser owns the finish.
 * Search for Hidden Inventory first logs its search row and applies Searching (janus.php ~L1766).
 * Resources settle at finish. Summons retain their creature, ground point, banked spots, free/charged mode and selected level in the
 * existing {t,f,i,s,p,l} storage; the finish uses the same summon planner as an immediate cast.
 */
export function timedCastStart(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  creatureTypeCastPreflight(ctx, ability, targets);
  const summoning = isSummonAbility(ctx, ability) && !!ctx.read.spawnSummons;
  const ritual = isRitualSummonStart(ctx, input, ability);
  const v1 = input.useAbilityVariable1;
  const freeMode = summoning && (ctx.itemCast || isSpellGrantCast(ctx, ability, caster, input)) ? String(v1) : '';
  if (v1 != null && v1 !== '' && !/^\d+$/.test(String(v1)) && !freeMode
      && !ritual && !(summoning && isSummonPickVariable(ctx, ability, v1, input.useAbilityVariable2))) handBack('casting_roll:variable');
  if (input.useAbilityVariable2 != null && input.useAbilityVariable2 !== '' && !freeMode && !ritual) handBack('casting_roll:variable-2');
  if (isSummonAbility(ctx, ability) && !summoning)
    handBack('casting_roll:summon-storage');
  if (!ecId(caster)) handBack('casting_roll:caster-not-member');
  const abilityId = upper(input.abilityId);
  // janus.php's custom block runs these by-id handlers BEFORE casting_roll (speed types, the custom-ability registry, the summon controls,
  // Spot Hidden Creatures — Codex A9 r3): a timed one would do both → PHP
  if (abilityId in CUSTOM_TYPES || abilityId in SUMMON_CONTROL_IDS || abilityId === SPOT_HIDDEN_CREATURES
      || String(itemField(ability, 'item_type') ?? '') === 'speed_type') handBack('casting_roll:by-id-handler');
  // The page pre-warms these implicit START statuses; an unavailable definition still refuses before any draw.
  for (const id of ritual ? [RITUAL_STATUS, CONCENTRATING]
      : abilityId === SEARCH_FOR_HIDDEN_INVENTORY ? [SEARCHING_STATUS, CASTING_STATUS] : [CASTING_STATUS])
    if (!ctx.read.definition(id)) handBack(`casting_roll:definition-not-held:${id}`);
  const point = summoning ? ctx.summonRequest?.point : null;
  const inventorySource = input.consumeInventoryId ? { u: upper(input.consumeInventoryId),
    ...(input.consumeInventoryUnit ? { c: true } : {}) } : {};
  if (typeof input.selection !== 'string' && !point) handBack('casting_roll:selection-not-held');
  const selection = point ? `${upper(ecLean(caster, 'encounter_id'))}|${point.x}:${point.y}:${point.z}`
    : String(input.selection ?? '').replace(/,+$/, '');
  if (selection.length > 16384) handBack('casting_roll:selection-too-long');
  selectionBoundToEncounter(ctx, selection);
  const storage = summoning ? JSON.stringify({ t: selection, s: summonCreatureId(ctx, input, ability),
    ...(ctx.summonRequest?.points.length ? { p: ctx.summonRequest.points } : {}),
    ...(freeMode ? { f: freeMode, ...(input.useAbilityVariable2 ? { i: upper(input.useAbilityVariable2) } : {}) } : {}),
    ...(ctx.castLevel == null ? {} : { l: ctx.castLevel }), ...inventorySource })
    : ctx.castLevel == null && !input.consumeInventoryId ? selection : JSON.stringify({ t: selection,
      ...(ctx.castLevel == null ? {} : { l: ctx.castLevel }), ...inventorySource });
  if (storage.length > 16384) handBack('casting_roll:selection-too-long');
  // get_status_effect_duration_times(…, use_ability_variable_1: null): the CAST time (both the Searching and the Casting rows)
  // Explicit ritual mode uses the shared ten-minute override, independent of authored cast time
  // (docs/FT3D_MAGIC_VFX.md). Its concentration lasts until the connected activity is retired.
  const ritualClock = ritual ? ctx.read.campaignClock() : null;
  if (ritual && !ritualClock) handBack('casting_ritual:campaign-clock-not-held');
  const ritualEnd = ritualClock ? ctx.read.addCampaignSeconds(ritualClock, 600) : null;
  const ends = ritualEnd ? { ends: [ritualEnd.year, ritualEnd.month, ritualEnd.day, ritualEnd.hour, ritualEnd.minute, ritualEnd.second].join('-'),
    flags: [0, 0, 0, 0] as [number, number, number, number] } : getStatusEffectDurationTimes(ctx, ability, caster, true);
  if (abilityId === SEARCH_FOR_HIDDEN_INVENTORY) {
    insertRoll(ctx, { roll_type_1: 'search_for_hidden_inventory_search', roll_item_id: abilityId, by: caster });
    applyStatusEffect(ctx, input, ability, SEARCHING_STATUS, '', caster, caster, ends, false, false);
    stampStorage(ctx, SEARCHING_STATUS, caster, storage);
  }
  // process_action_points(aps_ability: 3) — the ENCOUNTER's in_combat (not the caster's membership), never inside a trigger frame
  const inCombat = Number(record(ctx.encounter.encounters)?.in_combat ?? ctx.encounter.in_combat ?? 0) === 1;
  if (!ctx.trigger && inCombat) {
    const aps = requiredInt(ecLean(caster, 'aps'), 'casting_roll:aps-not-held') - 3;
    if (aps < 0) refuse('Not enough action points!');
    ec(caster).aps = aps;
  }
  if (ritual) applyStatusEffect(ctx, input, ability, CONCENTRATING, '', caster, caster,
    { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, false, false);
  const status = ritual ? RITUAL_STATUS : CASTING_STATUS;
  applyStatusEffect(ctx, input, ability, status, '', caster, caster, ends, false, false);
  stampStorage(ctx, status, caster, storage);
  insertRoll(ctx, { roll_type_1: ritual ? 'casting_ritual' : 'casting', roll_type_2: abilityId, by: caster });
}

/** Lane A10 (coordinator Codex Round 17 P2): ability_truth_commit_inc.php binds a NON-zone stored selection to the encounter — every
 *  `<encounter>|coords` token names THIS encounter, every bare id is a creature row of it or an inventory row held in it. A selection the
 *  commit would refuse hands back here instead (pre-draw — a timed start draws nothing), so the page never posts a verdict the commit 400s:
 *  a bare id the page cannot place on its roster / floor (an item inside a creature's pack) goes to PHP. */
function selectionBoundToEncounter(ctx: JanusCtx, storage: string): void {
  const eid = upper(record(ctx.encounter.encounters)?.encounter_id ?? ctx.encounter.encounter_id);
  const rowIds = new Set<string>();
  for (const row of Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr as Row[] : [])
    rowIds.add(upper(record(row.encounter_creatures)?.encounter_creature_id));
  for (const row of Array.isArray(ctx.encounter.encounter_inventories_arr) ? ctx.encounter.encounter_inventories_arr as Row[] : [])
    rowIds.add(upper(record(row.inventory)?.inventory_id ?? row.inventory_id));
  // the commit refuses more than 256 distinct bare ids (Codex A10 r2 parity)
  if (new Set(storage.split(',').map((t) => upper(t.trim())).filter((t) => t !== '' && !t.includes('|'))).size > 256) handBack('casting_roll:selection-too-many-ids');
  for (const raw of storage.split(',')) {
    const token = raw.trim();
    if (token === '') continue;
    const bar = token.indexOf('|');
    if (bar >= 0) {
      if (!eid || upper(token.slice(0, bar)) !== eid || !/^[-+0-9.eE:]{1,400}$/.test(token.slice(bar + 1).trim())) handBack('casting_roll:selection-outside-encounter');
    } else if (!/^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/.test(token)) handBack('casting_roll:selection-shape');
    else if (!rowIds.has(upper(token))) handBack('casting_roll:selection-off-roster');
  }
}

/** The status row this start just added for `statusId` on the caster carries the selection (an empty one is stored NULL). */
function stampStorage(ctx: JanusCtx, statusId: string, caster: Row, storage: string): void {
  const row = [...ctx.statusesAdded].reverse().find((s) => upper(s.status_item_id) === statusId && s.target === ecId(caster));
  if (!row) handBack('casting_roll:status-not-held'); // an existing same-item row (dedupe) — not modelled
  (row as unknown as Record<string, unknown>).status_casting_targets_arr = storage === '' ? null : storage;
}

/** parse_status_casting_targets_storage, extended with the selected numeric level ('l'); old rows retain base-level settlement. */
export function parseTimedCastStorage(raw: unknown): { targetId: string; summonCreatureId: string; freeCastMode: string; freeCastSource: string; summonPoints: unknown[]; castLevel?: number; consumeInventoryId?: string; consumeInventoryUnit?: true } {
  const empty = { targetId: '', summonCreatureId: '', freeCastMode: '', freeCastSource: '', summonPoints: [] as unknown[] };
  if (typeof raw !== 'string' || !raw) return empty;
  let s = raw;
  if (/^[\s]*["\[]/.test(s)) {
    try {
      const decoded: unknown = JSON.parse(s);
      if (typeof decoded === 'string' && decoded) s = decoded;
      else if (Array.isArray(decoded) && typeof decoded[0] === 'string' && decoded[0]) s = decoded[0];
    } catch { /* PHP leaves invalid JSON as its original raw selection */ }
  }
  if (/^\s*\{/.test(s)) {
    try {
      const obj = record(JSON.parse(s));
      if (obj && 't' in obj) return { targetId: String(obj.t ?? ''), summonCreatureId: String(obj.s ?? ''),
        freeCastMode: String(obj.f ?? ''), freeCastSource: String(obj.i ?? ''),
        summonPoints: Array.isArray(obj.p) ? obj.p : record(obj.p) ? Object.values(obj.p as object) : [],
        ...('u' in obj ? { consumeInventoryId: upper(obj.u), ...(obj.c === true ? { consumeInventoryUnit: true as const } : {}) } : {}),
        ...('l' in obj ? { castLevel: Number(obj.l) } : {}) };
    } catch { /* raw selection */ }
  }
  return { ...empty, targetId: s };
}
